import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import { obsRpc } from "../../../api";
import { setErrorSnackBarMessage, setSnackBarMessage } from "../../systemSlice";
import { getBytes, getBytesNumber } from "../../../common/utils";
import {
  MinioDBConfig,
  MinioEnvVars,
  MinioImagePullSecrets,
  MinioImageRegistryConfig,
  MinioIngestConfig,
  MinioNodeSelector,
  MinioResourceRequirements,
  ObserveRpcsAddObserveBody,
} from "api/observeRpcApi";
import { loadGlobalSitesAsync } from "../../systemThunks";

export const addObserveAsync = createAsyncThunk(
  "observe/addObserveAsync",
  async (_, { getState, rejectWithValue, dispatch }) => {
    const state = getState() as RootState;

    const selectedSite = state.addObserve.selectedSite;
    const name = state.addObserve.name;
    const namespace = state.addObserve.namespace;
    const volumeSize = state.addObserve.volumeSize;
    const storageClass = state.addObserve.storageClass;
    const minioURL = state.addObserve.minioURL;
    const accessKey = state.addObserve.accessKey;
    const secretKey = state.addObserve.secretKey;
    const sizeFactor = state.addObserve.volumeSizeFactor;
    const registry = state.addObserve.imageRegistry;
    const registryUsername = state.addObserve.imageRegistryUsername;
    const registryPassword = state.addObserve.imageRegistryPassword;
    const image = state.addObserve.image;
    const resourceCPURequest = state.addObserve.resourcesCPURequest;
    const resourceMemoryRequest = state.addObserve.resourcesMemoryRequest;
    const resourceCPULimit = state.addObserve.resourcesCPULimit;
    const resourceMemoryLimit = state.addObserve.resourcesMemoryLimit;
    const imagePullPolicy = state.addObserve.imagePullPolicy;
    const imagePullSecrets = state.addObserve.imagePullSecrets;
    const envVars = state.addObserve.envVars;
    const ingest = state.addObserve.ingest;
    const nodeSelector = state.addObserve.nodeSelectorPairs;
    const tolerations = state.addObserve.tolerations;
    const dbEnabled = state.addObserve.dbEnabled;
    const volumeSizeDB = state.addObserve.volumeSizeDB;
    const storageClassDB = state.addObserve.storageClassDB;
    const tenantReference = state.addObserve.tenantReference;

    let localNodeSelector: MinioNodeSelector = {
      keyValueMap: nodeSelector.reduce(
        (acc, pair) => {
          if (pair.key !== "") {
            acc[pair.key] = pair.value;
          }
          return acc;
        },
        {} as { [key: string]: string },
      ),
    };

    let localIngestConfig: MinioIngestConfig = {
      url: ingest?.url,
      replicas: Number(ingest?.replicas),
      enableBackup: ingest?.enableBackup,
      backupInterval: ingest?.backupInterval,
      minioHostURL: ingest?.minioHostURL,
      minioRegion: ingest?.minioRegion,
      minioBucket: ingest?.minioBucket,
      minioBucketPrefix: ingest?.minioBucketPrefix,
    };

    let localEnvVars: MinioEnvVars = {
      vars: envVars,
    };

    let localImagePullSecrets: MinioImagePullSecrets = {
      secrets: imagePullSecrets,
    };

    let localImageName = image?.trim() !== "" ? image : undefined;
    // Define resources
    let localResourceRequirements: MinioResourceRequirements | undefined;
    if (
      resourceCPURequest ||
      resourceMemoryRequest ||
      resourceCPULimit ||
      resourceMemoryLimit
    ) {
      localResourceRequirements = {};
    }
    if (localResourceRequirements) {
      if (resourceCPURequest?.trim() !== "") {
        localResourceRequirements.requests = {
          cpu: resourceCPURequest,
        };
      }
      if (resourceCPULimit?.trim() !== "") {
        localResourceRequirements.limits = {
          cpu: resourceCPULimit,
        };
      }
      if (resourceMemoryRequest?.trim() !== "") {
        if (localResourceRequirements.requests && resourceMemoryRequest) {
          localResourceRequirements.requests.memory = getBytes(
            resourceMemoryRequest,
            "Gi",
            true,
          );
        }
      }
      if (resourceMemoryLimit !== "") {
        if (localResourceRequirements.limits && resourceMemoryLimit) {
          localResourceRequirements.limits.memory = getBytes(
            resourceMemoryLimit,
            "Gi",
            true,
          );
        }
      }
    }
    // Define Registry config
    let localImageRegistryConfig: MinioImageRegistryConfig | undefined;
    if (registry !== "" || registryUsername !== "" || registryPassword !== "") {
      localImageRegistryConfig = {
        registry: registry,
        username: registryUsername,
        password: registryPassword,
      };
    }

    let dbConfig: MinioDBConfig | undefined;

    if (dbEnabled) {
      dbConfig = {
        storageClass: storageClassDB,
        volumeSize: `${getBytesNumber(volumeSizeDB, sizeFactor, true)}`,
      };
    }

    const localCurrentSize = `${getBytesNumber(volumeSize, sizeFactor, true)}`;

    const request: ObserveRpcsAddObserveBody = {
      name,
      namespace,
      volumeSize: localCurrentSize,
      storageClass,
      resources: localResourceRequirements,
      image: localImageName,
      imagePullPolicy: imagePullPolicy,
      imagePullSecrets: localImagePullSecrets,
      imageRegistryConfig: localImageRegistryConfig,
      env: localEnvVars,
      ingestConfig: localIngestConfig,
      nodeSelector: localNodeSelector,
      tolerations: tolerations,
      db: dbConfig,
    };

    if (tenantReference) {
      request.tenantReference = tenantReference;
    } else {
      request.minioUrl = minioURL;
      request.accessKey = accessKey;
      request.secretKey = secretKey;
    }

    return obsRpc.api
      .addObserve(selectedSite, request)
      .then((res) => {
        dispatch(setSnackBarMessage("Added Observe Instance successfully"));
        dispatch(loadGlobalSitesAsync());
        return res.data;
      })
      .catch((err) => {
        console.error(err.error);
        dispatch(setErrorSnackBarMessage(err.error));
        return rejectWithValue(err.error);
      });
  },
);
